import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  DcButton,
  PixelFactoryContext,
  PixelModal,
  Section,
  SectionActions,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from 'pixel-base'
import styled, { css } from 'styled-components'
import { del, get } from 'utils/api-routes.util'
import CheckIcon from 'utils/icons/check-icon'
import XmarkIcon from 'utils/icons/xmark-icon'

import AddUpdateBankAccount from './components/AddUpdateBankAccount'
import { IPixelFactory } from '../../../../../decl.d'
import FinancePageShell from '../../components/FinancePageShell'
interface IBankAccountDetail {
  bank_id: string
  account_number: string
  bank_name: string
  iban: string
  account_title: string
  branch_code: string
  account_type: string
  is_business_account: string
  bank_short_name: string
  swift_code: string
  banking_country: string
  is_deleted: boolean
  is_default: boolean
  is_active: boolean
}
const BankAccountDetail = () => {
  const params = useParams()
  const navigate = useNavigate()
  const BankDetailsRef = React.useRef(null)
  const bankID = params.id
  const {
    showLoader,
    hideLoader,
    showSuccess,
    showFailure,
    pixelConfirm,
    showPageLoader,
    hidePageLoader,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [countries, setCountries] = React.useState({
    list: [],
    default: '',
  })
  const getAllCountries = () => {
    get('/settings/options/countries').subscribe((res: any) => {
      setCountries({
        list: res.response.data,
        default: res.response._meta?.default,
      })
    })
  }
  React.useEffect(() => {
    getAllCountries()
  }, [])
  const [showUpdateModal, setShowUpdateModal] = React.useState(false)
  const [details, setDetails] = React.useState<IBankAccountDetail>({
    bank_id: '',
    account_number: '',
    bank_name: '',
    iban: '',
    account_title: '',
    branch_code: '',
    account_type: '',
    is_business_account: '',
    bank_short_name: '',
    swift_code: '',
    banking_country: '',
    is_deleted: '',
    is_default: '',
    is_active: '',
  })

  const readData = (id) => {
    showLoader(BankDetailsRef)
    get(`/settings/bank-accounts/${id}`).subscribe((res) => {
      setDetails(res.response.data)
      hideLoader(BankDetailsRef)
    })
  }

  React.useEffect(() => {
    readData(bankID)
  }, [bankID])
  const deleteData = () => {
    pixelConfirm({
      title: 'Delete Bank Account',
      message: 'Are you sure? you want to delete this Bank Account.',
      onConfirm: () => {
        showPageLoader()
        del(`/settings/bank-accounts/${bankID}/delete`).subscribe((res) => {
          hidePageLoader()
          if (res.response.message === 'Bank account deleted successfully') {
            showSuccess('Bank account deleted successfully')
            navigate('/finance/settings/bank-accounts')
          } else {
            showFailure(`Something went wrong, ${res.response.error}`)
          }
        })
      },
    })
  }

  return (
    <>
      <FinancePageShell
        title="Bank Account Details"
        breadcrumbs={[
          { name: 'Finance', active: false },
          { name: 'Settings', active: false },
          { name: 'Bank Accounts', active: false },
          { name: 'Bank Account Details', active: true },
        ]}
      >
        <BankAccountContainer ref={BankDetailsRef}>
          <Section>
            <SectionHeader>
              <SectionTitle>Bank Account Details</SectionTitle>
              <SectionActions>
                <DcButton
                  style={{
                    marginRight: '10px',
                    marginLeft: '10px',
                  }}
                  onClick={() => {
                    setShowUpdateModal(true)
                  }}
                  variant="outline"
                >
                  Update
                </DcButton>
                <DcButton onClick={deleteData} variant="outline">
                  Delete
                </DcButton>
              </SectionActions>
            </SectionHeader>
            <SectionContent isMobile={isMobile}>
              <InfoItem>
                <Label>Active Status:</Label>
                <Detail>
                  {details.is_active ? (
                    <CheckIcon color="green" />
                  ) : (
                    <XmarkIcon color="red" />
                  )}
                </Detail>
              </InfoItem>

              <InfoItem>
                <Label>Bank Name:</Label>
                <Detail>{details.bank_name ? details.bank_name : '--'}</Detail>
              </InfoItem>

              <InfoItem>
                <Label>Account Title:</Label>
                <Detail>
                  {details.account_title ? details.account_title : '--'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Account Number:</Label>
                <Detail>
                  {details.account_number ? details.account_number : '--'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Account Type:</Label>
                <Detail>
                  {details.account_type ? details.account_type : '--'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Account Short Name:</Label>
                <Detail>
                  {details.bank_short_name ? details.bank_short_name : '--'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Banking Country:</Label>
                <Detail>
                  {details.banking_country
                    ? details.banking_country === 'US'
                      ? 'United States'
                      : details.banking_country === 'PK' && 'Pakistan'
                    : '--'}
                </Detail>
              </InfoItem>

              <InfoItem>
                <Label>Branch Code:</Label>
                <Detail>
                  {details.branch_code ? details.branch_code : '--'}
                </Detail>
              </InfoItem>

              <InfoItem>
                <Label>IBAN:</Label>
                <Detail>{details.iban ? details.iban : '--'}</Detail>
              </InfoItem>

              <InfoItem>
                <Label>
                  {details.banking_country === 'US'
                    ? 'BIC Code:'
                    : 'Swift Code:'}
                </Label>
                <Detail>
                  {details.swift_code ? details.swift_code : '--'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Account Classification:</Label>
                <Detail>
                  {details.is_business_account === '1'
                    ? 'Business Account'
                    : 'Personal Account'}
                </Detail>
              </InfoItem>
              <InfoItem>
                <Label>Default Account:</Label>
                <Detail>
                  {details.is_default ? (
                    <CheckIcon color="green" />
                  ) : (
                    <XmarkIcon color="red" />
                  )}
                </Detail>
              </InfoItem>
            </SectionContent>
          </Section>
        </BankAccountContainer>
      </FinancePageShell>
      {showUpdateModal && (
        <PixelModal
          title="Update Bank Account"
          handleClose={() => {
            setShowUpdateModal(false)
          }}
          size={'lg'}
          show={showUpdateModal}
        >
          <AddUpdateBankAccount
            alreadyAdded={true}
            bankID={bankID}
            details={details}
            countries={countries}
            handleClose={(action, data) => {
              if (action === 'update') {
                if (data) {
                  setDetails(data)
                  setShowUpdateModal(false)
                }
              } else {
                setShowUpdateModal(false)
              }
            }}
          />
        </PixelModal>
      )}
    </>
  )
}
const BankAccountContainer = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: #f2f7f8;
  padding: 20px;
  overflow: auto;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;
    `}
`

const InfoItem = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
  &:last-child {
    border-bottom: none;
  }
`
const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #8e959b;
  display: table-cell;
  vertical-align: middle;
  padding-right: 10px;
  padding: 10px;
  background-color: #e9ecef;
  width: 40%;
`
const Detail = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #000;
  display: table-cell;
  vertical-align: middle;
  padding-left: 10px;
  width: 60%;
  background-color: #e9ecef78;
`

export default BankAccountDetail
